<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'CompanyBankAccount',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
    parent: {
      type: Object,
      required: true,
    },
    item: {
      type: Object,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const resource = ref(null);
  const editor = ref(null);
  const createTitle = ref(
    $fn.value.pgettext('CompanyBankAccounts', 'Add bank account')
  );
  const editTitle = ref(
    $fn.value.pgettext('CompanyBankAccounts', 'Edit bank account')
  );
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'md',
  }));

  const $data = computed(() => ({
    resource: resource.value,
    editor: editor.value,
    createTitle: createTitle.value,
    editTitle: editTitle.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
  }));

  const $$load_resource = () => {
    return props.item || $fn.value.schemaDefaults('CreateBankAccount');
  };
  const $$load_editor = () => {
    return resource.value.editor();
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'resource' },
      { name: 'editor', dependsOn: ['resource'] },
      { name: 'createTitle' },
      { name: 'editTitle' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
    ],
    loaders: { resource: $$load_resource, editor: $$load_editor },
    ux: {},
    dataRefs: {
      resource,
      editor,
      createTitle,
      editTitle,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const pageDocument = computed(() => {
    if (loaded.value) {
      return {
        title: props.item == null ? createTitle.value : editTitle.value,
      };
    }
    return null;
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.parent,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('parent');
    }
  );
  watch(
    () => props.item,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('item');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_yingovaee_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_1040167445267876_closeComponent_l3wf9ff04_getActionArgs = async (
    $event
  ) => {
    return {
      result: {
        value: $event.data.response.data,
      },
    };
  };
  const act_1040167445267876_closeComponent_k7bb4a3ee_getActionArgs = async (
    $event
  ) => {
    return {
      result: {},
    };
  };
  const act_1040167445267876_rest_wyasjokfh_getActionArgs = async ($event) => {
    return {
      method: 'save',
      container: props.parent.bank_accounts,
      resource: editor.value,
    };
  };

  const act_7315092382398562_closeModal_yingovaee =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_yingovaee_getActionArgs,
    });
  const inst_evh_7315092382398562_close_mdagiylcn =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_yingovaee,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_mdagiylcn],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_a7gep3l8h =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_dhxncgxo6 =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_a7gep3l8h,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_dhxncgxo6],
    event: 'reload',
  });
  const act_8564662037462133_closeComponent_l0hvg8dov =
    new actionTypes.CloseComponentAction({});
  const inst_evh_8564662037462133_close_l92b0l0jw =
    new actionTypes.EventHandlerImpl({
      action: act_8564662037462133_closeComponent_l0hvg8dov,
      event: 'close',
      displayName: 'closeComponent',
    });
  const inst_evh_8564662037462133_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8564662037462133_close_l92b0l0jw],
    event: 'close',
  });
  const act_1040167445267876_closeComponent_l3wf9ff04 =
    new actionTypes.CloseComponentAction({
      actionArgs: act_1040167445267876_closeComponent_l3wf9ff04_getActionArgs,
    });
  const inst_evh_1040167445267876_saved_1oow38j14 =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_closeComponent_l3wf9ff04,
      event: 'saved',
      displayName: 'closeComponent',
    });
  const act_1040167445267876_closeComponent_k7bb4a3ee =
    new actionTypes.CloseComponentAction({
      actionArgs: act_1040167445267876_closeComponent_k7bb4a3ee_getActionArgs,
    });
  const inst_evh_1040167445267876_deleted_ya4rga4ck =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_closeComponent_k7bb4a3ee,
      event: 'deleted',
      displayName: 'closeComponent',
    });
  const act_1040167445267876_rest_wyasjokfh = new actionTypes.RestAction({
    actionArgs: act_1040167445267876_rest_wyasjokfh_getActionArgs,
    displayName: 'submit',
    events: [
      inst_evh_1040167445267876_saved_1oow38j14,
      inst_evh_1040167445267876_deleted_ya4rga4ck,
    ],
  });
  const inst_evh_1040167445267876_submit_057tmn63l =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_rest_wyasjokfh,
      event: 'submit',
      displayName: 'submit',
    });
  const inst_evh_1040167445267876_submit = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_1040167445267876_submit_057tmn63l],
    event: 'submit',
  });
  const act_2434514588044777_closeComponent_0vlb0uq3c =
    new actionTypes.CloseComponentAction({});
  const inst_evh_2434514588044777_clickPreventStop_or6bm6v2r =
    new actionTypes.EventHandlerImpl({
      action: act_2434514588044777_closeComponent_0vlb0uq3c,
      event: 'click.prevent.stop',
      displayName: 'closeComponent',
    });
  const inst_evh_2434514588044777_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2434514588044777_clickPreventStop_or6bm6v2r],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_7315092382398562_close_mdagiylcn,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_dhxncgxo6,
    inst_evh_7315092382398562_reload,
    inst_evh_8564662037462133_close_l92b0l0jw,
    inst_evh_8564662037462133_close,
    inst_evh_1040167445267876_saved_1oow38j14,
    inst_evh_1040167445267876_deleted_ya4rga4ck,
    inst_evh_1040167445267876_submit_057tmn63l,
    submit: inst_evh_1040167445267876_submit_057tmn63l,
    inst_evh_1040167445267876_submit,
    inst_evh_2434514588044777_clickPreventStop_or6bm6v2r,
    inst_evh_2434514588044777_clickPreventStop,
  };

  const evh_7315092382398562_close_mdagiylcn = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_mdagiylcn.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_dhxncgxo6 = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_dhxncgxo6.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close_l92b0l0jw = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close_l92b0l0jw.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_saved_1oow38j14 = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_saved_1oow38j14.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_deleted_ya4rga4ck = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_deleted_ya4rga4ck.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit_057tmn63l = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit_057tmn63l.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop_or6bm6v2r = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop_or6bm6v2r.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="CompanyBankAccount"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <template #header>
      <UcHeader
        :card="!modal"
        :modal="modal"
        :title="documentTitle"
        v-if="loaded"
        @close="evh_8564662037462133_close($event, {})"
      >
      </UcHeader>
    </template>
    <UcBody :card="!modal" :modal="modal" v-if="loaded">
      <ItpForm
        name="bankAccount"
        @submit="evh_1040167445267876_submit($event, {})"
      >
        <UcCompanyBankAccountFieldset :bankAccount="editor">
        </UcCompanyBankAccountFieldset>
      </ItpForm>
    </UcBody>
    <template #footer>
      <UcFooter :card="!modal" :modal="modal" v-if="loaded">
        <ItpButton
          :form="forms.bankAccount"
          :text="gettext('Save')"
          type="submit"
          :spinning="actions.submit.isRunning"
        >
        </ItpButton>
        <ItpButton
          :text="gettext('Close')"
          variant="default"
          :disabled="actions.submit.isRunning"
          @click.prevent.stop="
            evh_2434514588044777_clickPreventStop($event, {})
          "
        >
        </ItpButton>
      </UcFooter>
    </template>
  </ItpPage>
</template>
